import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { SyncAlertSettings } from "@/components/SyncAlertSettings";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle2, Database, Activity, FileText, Receipt, Users, Terminal, Link2, PlugZap, Unplug, Trash2, History } from "lucide-react";

const RESOURCES = [
  { icon: Users, label: "Patient Demographics" },
  { icon: Activity, label: "Appointments" },
  { icon: FileText, label: "Clinical Notes" },
  { icon: Receipt, label: "Billing Status" },
];

export default function DrChronoSync() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const pollRef = useRef(null);
  const { data: status } = useQuery({ queryKey: ["drchrono-status"], queryFn: async () => (await api.get("/drchrono/status")).data });
  const { data: syncs = [] } = useQuery({ queryKey: ["drchrono-syncs"], queryFn: async () => (await api.get("/drchrono/syncs")).data });
  const connected = status?.connected;

  useEffect(() => {
    if (params.get("connected")) {
      toast.success("DrChrono account connected");
      qc.invalidateQueries({ queryKey: ["drchrono-status"] });
      setParams({}, { replace: true });
    } else if (params.get("error")) {
      toast.error(`DrChrono connection failed: ${params.get("error")}`);
      setParams({}, { replace: true });
    }
  }, [params, setParams, qc]);

  const connect = useMutation({
    mutationFn: async () => (await api.get("/drchrono/authorize-url")).data,
    onSuccess: (d) => {
      const w = window.open(d.url, "_blank");
      if (!w) {
        toast.error("Pop-up blocked. Please allow pop-ups for this site, then click Connect again.");
        return;
      }
      try { w.opener = null; } catch { /* noop */ }
      toast.info("Finish signing in to DrChrono in the new tab…");
      const started = Date.now();
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        if (Date.now() - started > 180000) { clearInterval(pollRef.current); pollRef.current = null; return; }
        try {
          const s = (await api.get("/drchrono/status")).data;
          if (s?.connected) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            toast.success("DrChrono account connected");
            qc.invalidateQueries({ queryKey: ["drchrono-status"] });
          }
        } catch { /* keep polling */ }
      }, 3000);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start DrChrono connection"),
  });

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/drchrono/disconnect")).data,
    onSuccess: () => { toast.success("DrChrono disconnected"); qc.invalidateQueries({ queryKey: ["drchrono-status"] }); },
  });

  const clearDemo = useMutation({
    mutationFn: async () => (await api.post("/admin/clear-demo-data")).data,
    onSuccess: (d) => {
      toast.success(`Removed ${d.patients_removed} sample patients & ${d.appointments_removed} appointments`);
      qc.invalidateQueries({ queryKey: ["patients"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["drchrono-status"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not clear demo data"),
  });

  const sync = useMutation({
    mutationFn: async () => (await api.post("/drchrono/sync")).data,
    onMutate: () => setLogs([]),
    onSuccess: (d) => {
      (d.logs || []).forEach((l, i) => setTimeout(() => setLogs((prev) => [...prev, l]), i * 420));
      setTimeout(() => {
        toast.success(`Sync complete · ${d.patients_updated} patients, ${d.appointments_updated} appointments`);
        qc.invalidateQueries({ queryKey: ["drchrono-status"] });
        qc.invalidateQueries({ queryKey: ["drchrono-syncs"] });
        qc.invalidateQueries({ queryKey: ["drchrono-sync-status"] });
        qc.invalidateQueries({ queryKey: ["patients"] });
        qc.invalidateQueries({ queryKey: ["appointments"] });
      }, (d.logs?.length || 1) * 420);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed"),
  });

  return (
    <Layout title="DrChrono EMR Integration" subtitle="Live OAuth connection · sync patients, appointments & billing"
      actions={
        connected ? (
          <div className="flex items-center gap-2">
            <Button data-testid="drchrono-reconnect-button" variant="outline" onClick={() => connect.mutate()} disabled={connect.isPending}>
              <Link2 className="h-4 w-4 mr-2" /> Reconnect
            </Button>
            <Button data-testid="drchrono-full-sync-button" onClick={() => sync.mutate()} disabled={sync.isPending}
              className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <RefreshCw className={`h-4 w-4 mr-2 ${sync.isPending ? "animate-spin" : ""}`} /> Run Full Sync
            </Button>
          </div>
        ) : (
          <Button data-testid="connect-drchrono-button" onClick={() => connect.mutate()} disabled={connect.isPending}
            className="bg-cyan-600 hover:bg-cyan-700 text-white">
            <Link2 className="h-4 w-4 mr-2" /> Connect DrChrono
          </Button>
        )
      }
    >
      {!connected && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          data-testid="drchrono-connect-card"
          className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm mb-6 text-center">
          <span className="h-14 w-14 rounded-2xl bg-cyan-50 border border-cyan-200 flex items-center justify-center mx-auto mb-4">
            <PlugZap className="h-7 w-7 text-cyan-600" />
          </span>
          <h3 className="font-heading text-xl font-bold text-slate-900">Connect your DrChrono account</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Authorize The Men's Clinic to securely read patient demographics and the appointment
            schedule from DrChrono. You'll be redirected to DrChrono to approve access.
          </p>
          <Button data-testid="connect-drchrono-cta" onClick={() => connect.mutate()} disabled={connect.isPending || status?.configured === false}
            className="mt-5 bg-slate-900 hover:bg-slate-800 text-white">
            <Link2 className="h-4 w-4 mr-2" /> {connect.isPending ? "Redirecting…" : "Connect DrChrono"}
          </Button>
          {status?.configured === false && (
            <p className="text-xs text-red-500 mt-3">API credentials are not configured on the server.</p>
          )}
        </motion.div>
      )}

      {connected && status?.needs_reauth && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          data-testid="drchrono-scopes-banner"
          className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <PlugZap className="h-5 w-5 text-cyan-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">New permissions needed.</span> To sync real
            provider names and procedure prices, click <span className="font-semibold">Reconnect</span> and
            approve {(status?.missing_scopes?.length ? status.missing_scopes : ["user:read", "billing:read"]).map((s, i, arr) => (
              <span key={s}><code className="text-cyan-700">{s}</code>{i < arr.length - 1 ? " and " : ""}</span>
            ))} on DrChrono. If these aren't offered on the approval screen, enable them in your DrChrono developer app's Scopes list first.
          </div>
        </motion.div>
      )}
      {connected && status && !status.needs_reauth && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          data-testid="drchrono-scopes-ok"
          className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-6 flex items-center gap-2.5">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <div className="text-sm text-slate-700">
            All permissions granted — provider names and procedure prices will sync.
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <div className="bg-slate-950 text-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className={`absolute inline-flex h-full w-full rounded-full ${connected ? "bg-emerald-400 pulse-dot" : "bg-slate-500"}`} />
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${connected ? "bg-emerald-500" : "bg-slate-600"}`} />
                </span>
                <span className={`text-sm font-semibold ${connected ? "text-emerald-300" : "text-slate-400"}`}>
                  {connected ? "Connected" : "Not Connected"}
                </span>
              </div>
              {connected && (
                <button data-testid="drchrono-disconnect-button" onClick={() => disconnect.mutate()}
                  className="text-xs text-slate-500 hover:text-red-400 flex items-center gap-1 transition-colors">
                  <Unplug className="h-3.5 w-3.5" /> Disconnect
                </button>
              )}
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">API Status</dt><dd className={connected ? "text-emerald-300 font-semibold" : "text-slate-500"}>{connected ? "Live" : "Offline"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Latency</dt><dd className="font-mono-metric text-cyan-300">{status?.latency_ms ? `${status.latency_ms}ms` : "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Patients Synced</dt><dd className="font-mono-metric">{status?.patients_synced ?? 0}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Appointments</dt><dd className="font-mono-metric">{status?.appointments_synced ?? 0}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Last Synced</dt><dd className="text-slate-300 text-xs">{status?.last_synced ? new Date(status.last_synced).toLocaleString() : "Never"}</dd></div>
            </dl>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2 mb-4">
              <Database className="h-4 w-4 text-cyan-500" /> Synced Resources
            </h3>
            <div className="space-y-2.5">
              {RESOURCES.map((r) => (
                <div key={r.label} className="flex items-center gap-3 text-sm">
                  <r.icon className="h-4 w-4 text-slate-400" />
                  <span className="flex-1 text-slate-700">{r.label}</span>
                  <CheckCircle2 className={`h-4 w-4 ${connected ? "text-emerald-500" : "text-slate-300"}`} />
                </div>
              ))}
            </div>
          </div>

          <button data-testid="clear-demo-data-button" disabled={clearDemo.isPending}
            onClick={() => { if (window.confirm("Remove all sample demo patients and their appointments? Your real DrChrono records are kept.")) clearDemo.mutate(); }}
            className="w-full text-xs text-slate-500 hover:text-red-600 border border-slate-200 rounded-lg py-2.5 flex items-center justify-center gap-1.5 transition-colors">
            <Trash2 className="h-3.5 w-3.5" /> {clearDemo.isPending ? "Clearing…" : "Clear sample demo data"}
          </button>
        </div>

        <div className="lg:col-span-2 bg-slate-950 rounded-xl p-6 shadow-sm flex flex-col min-h-[420px]">
          <div className="flex items-center gap-2 mb-4 border-b border-slate-800 pb-3">
            <Terminal className="h-4 w-4 text-cyan-400" />
            <span className="text-sm font-semibold text-slate-200 font-mono-metric">drchrono-sync — execution log</span>
          </div>
          <div className="flex-1 font-mono-metric text-xs space-y-2">
            {logs.length === 0 && !sync.isPending && (
              <p className="text-slate-500">{connected ? 'Press "Run Full Sync" to pull the latest data from DrChrono.' : "Connect your DrChrono account to begin syncing."}</p>
            )}
            {sync.isPending && logs.length === 0 && (
              <p className="text-cyan-400 animate-pulse">Establishing secure connection to DrChrono API…</p>
            )}
            <AnimatePresence>
              {logs.map((l, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-2 text-slate-300">
                  <span className="text-emerald-400">✓</span>
                  <span className="text-slate-500">[{String(i + 1).padStart(2, "0")}]</span>
                  <span>{l.step}{l.detail ? <span className="text-cyan-400"> — {l.detail}</span> : ""}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <p className="text-[11px] text-slate-600 mt-4 pt-3 border-t border-slate-800">
            Live DrChrono OAuth integration. Tokens are stored encrypted server-side and refreshed automatically.
          </p>
        </div>
      </div>

      <div data-testid="sync-history" className="bg-white border border-slate-200 rounded-xl shadow-sm mt-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <History className="h-4 w-4 text-cyan-500" />
          <h3 className="font-heading font-bold text-slate-900">Sync History</h3>
        </div>
        {syncs.length === 0 ? (
          <div className="px-6 py-6 text-sm text-slate-400">No syncs yet. Automatic daily sync runs once DrChrono is connected.</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {syncs.map((s, i) => (
              <div key={i} className="px-6 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.trigger === "scheduled" ? "bg-cyan-50 text-cyan-700" : "bg-slate-100 text-slate-600"}`}>{s.trigger}</span>
                  <span className="text-slate-500">{new Date(s.ran_at).toLocaleString()}</span>
                </div>
                {s.status === "failed"
                  ? <div className="text-red-600 text-xs truncate max-w-[50%]" title={s.error}>Failed · {s.error}</div>
                  : <div className="font-mono-metric text-slate-700">{s.patients} patients · {s.appointments} appts</div>}
              </div>
            ))}
          </div>
        )}
      </div>
      <SyncAlertSettings />
    </Layout>
  );
}
